import { useCallback, useMemo } from "react";
import useSWR from "swr";
import { auth, hvoFirestore, vceFirestore } from "../../configs/FirebaseConfig";
import { FIRESTORE_PICK_DAY_LOCKS_TABLE, FIRESTORE_PICK_DAYS_TABLE } from "../../constants/FirestoreConstant";
import { PICK_DAY_GAP_DAYS } from "../../constants/PickingConstant";
import { deliveryDatesForPickDay, isPickDayLocked, releasePickDay, unlockPickDay } from "../../functions/pickDay";
import { fetchActiveWaves } from "./usePickWaves";
import { hasHvoSession } from "./useProductFields";

/**
 * A pick day: released (locked) or open, in the VCE project like all shared warehouse data.
 *
 * Releasing is what turns a day's orders into a frozen snapshot that rounds are built from, so
 * it also writes one `pickDayLocks/{deliveryDate}` per delivery date the day covers - in VCE and,
 * when there is a session, in HVO. Security rules cannot do date arithmetic or read the other
 * project, so the lock has to exist per delivery date in each project for the rules to enforce
 * it (planning/orderpicking-bouwplan-cms.md, besluit 9). A missing HVO session does not block the
 * release - the warehouse must not stand still for a login problem - but it is reported.
 */

const dayDocument = (pickDay) => vceFirestore.collection(FIRESTORE_PICK_DAYS_TABLE).doc(pickDay);

const fetchPickDay = async (pickDay) => {
  const snapshot = await dayDocument(pickDay).get();
  return snapshot.exists ? { id: snapshot.id, ...snapshot.data() } : null;
};

const currentUser = () => ({
  uid: (auth.currentUser && auth.currentUser.uid) || null,
  email: (auth.currentUser && auth.currentUser.email) || null,
});

const writeLocks = async (firestore, pickDay, deliveryDates, user, now) => {
  const batch = firestore.batch();
  deliveryDates.forEach((deliveryDate) =>
    batch.set(firestore.collection(FIRESTORE_PICK_DAY_LOCKS_TABLE).doc(deliveryDate), {
      deliveryDate,
      pickDay,
      lockedAt: now,
      lockedBy: user.email,
    }),
  );
  await batch.commit();
};

const removeLocks = async (firestore, deliveryDates) => {
  const batch = firestore.batch();
  deliveryDates.forEach((deliveryDate) => batch.delete(firestore.collection(FIRESTORE_PICK_DAY_LOCKS_TABLE).doc(deliveryDate)));
  await batch.commit();
};

export const usePickDays = (pickDay, { gapDays = PICK_DAY_GAP_DAYS } = {}) => {
  const { data, error, isLoading, mutate } = useSWR(pickDay ? ["pickDay", pickDay] : null, () => fetchPickDay(pickDay), {
    revalidateOnFocus: true,
    dedupingInterval: 5000,
  });

  const deliveryDates = useMemo(() => (pickDay ? deliveryDatesForPickDay(pickDay, { gapDays }) : []), [pickDay, gapDays]);

  /**
   * Release (lock) the day. Returns per project whether the locks were written, so the panel can
   * say "HVO-lock niet geschreven" instead of pretending the day is locked everywhere.
   */
  const release = useCallback(
    async ({ orderCount }) => {
      const user = currentUser();
      const now = Date.now();
      const existing = await fetchPickDay(pickDay);
      const next = releasePickDay(existing, { pickDay, ...user, now, orderCount, deliveryDates, gapDays });
      await dayDocument(pickDay).set(next);

      const locks = { VCE: false, HVO: false };
      await writeLocks(vceFirestore, pickDay, deliveryDates, user, now);
      locks.VCE = true;
      if (hasHvoSession()) {
        try {
          await writeLocks(hvoFirestore, pickDay, deliveryDates, user, now);
          locks.HVO = true;
        } catch (lockError) {
          console.error("[usePickDays] HVO lock could not be written:", lockError);
        }
      }
      await mutate();
      return { ok: true, locks };
    },
    [pickDay, deliveryDates, gapDays, mutate],
  );

  /**
   * Unlock the day. Refused while a round of this day is open or being picked: the picker is
   * walking the freezer with those orders, an edit now would change an order under their hands.
   * The rounds are read here rather than taken from the screen, which may not have loaded them;
   * if they cannot be read, this throws and nothing is unlocked.
   */
  const unlock = useCallback(
    async () => {
      const activeWaves = await fetchActiveWaves(pickDay);
      if (activeWaves.length > 0) return { ok: false, reason: "pick-day/active-waves", count: activeWaves.length };
      const user = currentUser();
      const existing = await fetchPickDay(pickDay);
      await dayDocument(pickDay).set(unlockPickDay(existing, { ...user, now: Date.now() }));
      const dates = (existing && existing.deliveryDates) || deliveryDates;
      await removeLocks(vceFirestore, dates);
      let hvoUnlocked = false;
      if (hasHvoSession()) {
        try {
          await removeLocks(hvoFirestore, dates);
          hvoUnlocked = true;
        } catch (lockError) {
          console.error("[usePickDays] HVO lock could not be removed:", lockError);
        }
      }
      await mutate();
      return { ok: true, locks: { VCE: true, HVO: hvoUnlocked } };
    },
    [pickDay, deliveryDates, mutate],
  );

  /**
   * The next "Ronde n" of this day, from a counter on the day document - a transaction, so two
   * people composing a round at the same moment never print the same number.
   */
  const nextWaveNumber = useCallback(async () => {
    const reference = dayDocument(pickDay);
    return vceFirestore.runTransaction(async (transaction) => {
      const snapshot = await transaction.get(reference);
      const current = snapshot.exists ? snapshot.data() : null;
      const next = ((current && current.waveCount) || 0) + 1;
      if (current) transaction.update(reference, { waveCount: next });
      else transaction.set(reference, { date: pickDay, status: "OPEN", waveCount: next, updatedAt: Date.now() });
      return next;
    });
  }, [pickDay]);

  return {
    pickDay,
    deliveryDates,
    doc: data || null,
    locked: isPickDayLocked(data),
    releasedAt: (data && data.releasedAt) || null,
    releasedBy: (data && data.releasedBy) || null,
    isLoading,
    error,
    refresh: mutate,
    release,
    unlock,
    nextWaveNumber,
  };
};
